import { useEffect } from 'react';

interface UseChipCloseEventsParams {
  allAdd: boolean;
  allRemove: boolean;
  chipRefs: React.RefObject<(HTMLElement | null)[]>;
  selectionEntries: [string, string][];
  handleRemoveAll: () => void;
  handleRemoveItem: (key: string) => void;
  hideSelectAllWhenSingleItem?: boolean;
  optionKeysLength: number;
}

/**
 * Attaches `chipClickableClose` event listeners to BDS chip elements,
 * wiring them to remove-all or remove-single handlers as appropriate.
 *
 * Extracted to reduce cognitive complexity of FilterSelector.
 */
export function useChipCloseEvents({
  allAdd,
  allRemove,
  chipRefs,
  selectionEntries,
  handleRemoveAll,
  handleRemoveItem,
  hideSelectAllWhenSingleItem = false,
  optionKeysLength,
}: UseChipCloseEventsParams) {
  useEffect(() => {
    const cleanups: (() => void)[] = [];

    const attachHandler = (el: HTMLElement, handler: (e: Event) => void) => {
      el.addEventListener('chipClickableClose', handler);
      cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
    };

    const showTodosChip = (allAdd || allRemove) && !(hideSelectAllWhenSingleItem && optionKeysLength === 1);

    if (showTodosChip) {
      const el = chipRefs.current[0];
      if (el) attachHandler(el, (e) => { e.stopPropagation(); handleRemoveAll(); });
    } else {
      selectionEntries.forEach(([key], index) => {
        const el = chipRefs.current[index];
        if (!el) return;
        attachHandler(el, (e) => { e.stopPropagation(); handleRemoveItem(key); });
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, [allAdd, allRemove, chipRefs, selectionEntries, handleRemoveAll, handleRemoveItem, hideSelectAllWhenSingleItem, optionKeysLength]);
}
